import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { walletApi } from '@/services/api/wallet';
import { showSnackbar } from './uiSlice';
import {
  WalletBalance,
  WalletTransaction,
  WithdrawalRequest,
  TransactionFilters,
  PaginatedTransactions,
  PaginatedWithdrawals,
  WalletStats,
  WalletAnalytics,
  DepositData,
  WithdrawalData,
  TransferData,
  WalletSettingsData,
  UserWallet,
  Currency,
} from '@/types/wallet';

interface WalletState {
  balance: WalletBalance | null;
  balances: WalletBalance[];
  transactions: PaginatedTransactions | null;
  withdrawals: PaginatedWithdrawals | null;
  stats: WalletStats | null;
  analytics: WalletAnalytics | null;
  settings: UserWallet | null;
  currentTransaction: WalletTransaction | null;
  currentWithdrawal: WithdrawalRequest | null;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
}

const initialState: WalletState = {
  balance: null,
  balances: [],
  transactions: null,
  withdrawals: null,
  stats: null,
  analytics: null,
  settings: null,
  currentTransaction: null,
  currentWithdrawal: null,
  loading: false,
  actionLoading: false,
  error: null,
};

const getErrorMessage = (error: any, defaultMessage: string): string => {
  return error.response?.data?.error?.message || defaultMessage;
};

export const fetchBalance = createAsyncThunk(
  'wallet/fetchBalance',
  async (currency: Currency | undefined, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.getBalance(currency);
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to fetch wallet balance');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const fetchAllBalances = createAsyncThunk(
  'wallet/fetchAllBalances',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.getAllBalances();
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to fetch wallet balances');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const fetchTransactions = createAsyncThunk(
  'wallet/fetchTransactions',
  async (
    { filters, page, limit }: { filters?: TransactionFilters; page?: number; limit?: number },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await walletApi.getTransactions(filters, page, limit);
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to fetch transactions');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const fetchTransaction = createAsyncThunk(
  'wallet/fetchTransaction',
  async (id: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.getTransaction(id);
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to fetch transaction details');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const initiateDeposit = createAsyncThunk(
  'wallet/initiateDeposit',
  async (data: DepositData, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.initiateDeposit(data);
      dispatch(showSnackbar({ message: 'Redirecting to payment gateway...', type: 'success' }));
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to initiate deposit');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const fetchDepositHistory = createAsyncThunk(
  'wallet/fetchDepositHistory',
  async ({ page, limit }: { page?: number; limit?: number }, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.getDepositHistory(page, limit);
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to fetch deposit history');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const requestWithdrawal = createAsyncThunk(
  'wallet/requestWithdrawal',
  async (data: WithdrawalData, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.requestWithdrawal(data);
      dispatch(
        showSnackbar({
          message: 'Withdrawal request submitted successfully. Awaiting admin approval.',
          type: 'success',
        })
      );
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to request withdrawal');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const fetchWithdrawalHistory = createAsyncThunk(
  'wallet/fetchWithdrawalHistory',
  async ({ page, limit }: { page?: number; limit?: number }, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.getWithdrawalHistory(page, limit);
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to fetch withdrawal history');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const fetchWithdrawal = createAsyncThunk(
  'wallet/fetchWithdrawal',
  async (id: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.getWithdrawal(id);
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to fetch withdrawal details');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const cancelWithdrawal = createAsyncThunk(
  'wallet/cancelWithdrawal',
  async (id: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.cancelWithdrawal(id);
      dispatch(showSnackbar({ message: 'Withdrawal cancelled successfully', type: 'success' }));
      return { id, message: response.data.message };
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to cancel withdrawal');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const transferFunds = createAsyncThunk(
  'wallet/transferFunds',
  async (data: TransferData, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.transferFunds(data);
      dispatch(showSnackbar({ message: 'Transfer completed successfully', type: 'success' }));
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to transfer funds');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const fetchTransferHistory = createAsyncThunk(
  'wallet/fetchTransferHistory',
  async ({ page, limit }: { page?: number; limit?: number }, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.getTransferHistory(page, limit);
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to fetch transfer history');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const fetchWalletSettings = createAsyncThunk(
  'wallet/fetchWalletSettings',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.getWalletSettings();
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to fetch wallet settings');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const updateWalletSettings = createAsyncThunk(
  'wallet/updateWalletSettings',
  async (data: WalletSettingsData, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.updateWalletSettings(data);
      dispatch(showSnackbar({ message: 'Settings updated successfully', type: 'success' }));
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to update wallet settings');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const fetchWalletStats = createAsyncThunk(
  'wallet/fetchWalletStats',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await walletApi.getWalletStats();
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to fetch wallet statistics');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const fetchWalletAnalytics = createAsyncThunk(
  'wallet/fetchWalletAnalytics',
  async (
    { startDate, endDate }: { startDate?: string; endDate?: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await walletApi.getWalletAnalytics(startDate, endDate);
      return response.data.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to fetch wallet analytics');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const exportTransactions = createAsyncThunk(
  'wallet/exportTransactions',
  async (
    { filters, format }: { filters?: TransactionFilters; format?: 'csv' | 'pdf' },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await walletApi.exportTransactions(filters, format);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `transactions_${new Date().toISOString().split('T')[0]}.${format || 'csv'}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(
        showSnackbar({ message: 'Transactions exported successfully', type: 'success' })
      );
      return true;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to export transactions');
      dispatch(showSnackbar({ message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentTransaction: (state) => {
      state.currentTransaction = null;
    },
    clearCurrentWithdrawal: (state) => {
      state.currentWithdrawal = null;
    },
    updateBalanceRealtime: (state, action: PayloadAction<WalletBalance>) => {
      state.balance = action.payload;
      const index = state.balances.findIndex((b) => b.currency === action.payload.currency);
      if (index !== -1) {
        state.balances[index] = action.payload;
      } else {
        state.balances.push(action.payload);
      }
    },
    addTransactionRealtime: (state, action: PayloadAction<WalletTransaction>) => {
      if (state.transactions) {
        state.transactions.transactions.unshift(action.payload);
      }
    },
    updateWithdrawalStatusRealtime: (
      state,
      action: PayloadAction<{ id: string; status: string }>
    ) => {
      if (state.withdrawals) {
        const withdrawal = state.withdrawals.withdrawals.find((w) => w._id === action.payload.id);
        if (withdrawal) {
          withdrawal.status = action.payload.status as any;
        }
      }
      if (state.currentWithdrawal && state.currentWithdrawal._id === action.payload.id) {
        state.currentWithdrawal.status = action.payload.status as any;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.balance = action.payload;
      })
      .addCase(fetchBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchAllBalances.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllBalances.fulfilled, (state, action) => {
        state.loading = false;
        state.balances = action.payload;
      })
      .addCase(fetchAllBalances.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload || {
          transactions: [],
          pagination: {
            page: 1,
            limit: 20,
            totalPages: 0,
            totalItems: 0,
            hasMore: false,
          },
        };
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTransaction = action.payload;
      })
      .addCase(fetchTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(initiateDeposit.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(initiateDeposit.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(initiateDeposit.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchDepositHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDepositHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload || {
          transactions: [],
          pagination: {
            page: 1,
            limit: 20,
            totalPages: 0,
            totalItems: 0,
            hasMore: false,
          },
        };
      })
      .addCase(fetchDepositHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(requestWithdrawal.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(requestWithdrawal.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(requestWithdrawal.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchWithdrawalHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWithdrawalHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.withdrawals = action.payload;
      })
      .addCase(fetchWithdrawalHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchWithdrawal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWithdrawal.fulfilled, (state, action) => {
        state.loading = false;
        state.currentWithdrawal = action.payload;
      })
      .addCase(fetchWithdrawal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(cancelWithdrawal.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(cancelWithdrawal.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(cancelWithdrawal.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(transferFunds.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(transferFunds.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(transferFunds.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchTransferHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransferHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload || {
          transactions: [],
          pagination: {
            page: 1,
            limit: 20,
            totalPages: 0,
            totalItems: 0,
            hasMore: false,
          },
        };
      })
      .addCase(fetchTransferHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchWalletSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWalletSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
      })
      .addCase(fetchWalletSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updateWalletSettings.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(updateWalletSettings.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.settings = action.payload;
      })
      .addCase(updateWalletSettings.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchWalletStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWalletStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchWalletStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchWalletAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWalletAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchWalletAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearCurrentTransaction,
  clearCurrentWithdrawal,
  updateBalanceRealtime,
  addTransactionRealtime,
  updateWithdrawalStatusRealtime,
} = walletSlice.actions;

export default walletSlice.reducer;
